<template>
  <tr class="hover:bg-gray-50 text-left">
    <td
      class="px-4 py-2 text-gray-900 text-sm font-medium leading-5 border-b border-gray-200"
    >
      <div class="flex flex-row gap-2">
        <div
          v-if="isExpandable()"
          class="flex flex-col items-center justify-center mr-2 cursor-pointer"
          @click="toggleExpand"
        >
          <div
            v-html="isExpanded ? ARROW_UP : ARROW_DOWN"
            class="w-4 h-4"
          ></div>
        </div>
        <div class="flex flex-col shrink-0">
          <img
            v-if="!isInvitation && hasProfilePicture()"
            :src="getProfilePicture()"
            alt="Profile Picture"
            class="w-10 h-10 rounded-full overflow-hidden"
          />
          <div
            v-else
            v-html="BLANK_PROFILE_PICTURE_ICON"
            class="w-10 h-10 rounded-full overflow-hidden"
          ></div>
        </div>
        <div class="flex flex-col">
          <span v-if="!isInvitation" class="flex flex-col gap-2">
            <a
              v-if="shouldShowExpertLink()"
              :href="getExpertCalendarUrl()"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {{ getExpertName() }}
            </a>
            <span v-else>{{ getExpertName() }}</span>
            <div
              v-if="getTitle() || getCompanyName()"
              class="text-xs text-gray-800"
            >
              <span v-if="getTitle()">{{ getTitle() }}</span>
              <span v-if="getTitle() && getCompanyName()"> at </span>
              <span v-if="getCompanyName()">{{ getCompanyName() }}</span>
            </div>
          </span>
        </div>
        <div class="flex flex-col" v-if="!isInvitation && hasLinkedinProfile()">
          <img
            src="@/assets/shared/linkedin-icon.svg"
            alt=""
            class="object-cover shrink-0 w-6 h-6 cursor-pointer"
            @click="handleLinkedinClick()"
          />
        </div>
        <div
          class="flex flex-col"
          v-if="!isInvitation && hasCrmContact()"
          @click="openCrmContact()"
        >
          <div
            v-if="getCrmType() === 'hubspot'"
            v-html="HUBSPOT_LOGO_ICON"
            class="object-cover shrink-0 w-6 h-6 cursor-pointer mt-1"
          ></div>
          <div v-if="getCrmType() === 'salesforce'">
            <img
              src="@/assets/shared/salesforce-logo.png"
              alt="Salesforce Logo"
              class="object-contain shrink-0 w-10 h-6 cursor-pointer"
            />
          </div>
        </div>
      </div>
    </td>
    <td
      class="px-4 py-2 text-gray-900 text-sm font-medium leading-5 border-b border-gray-200"
    >
      <div class="flex flex-col">
        <span>{{ getEmail() }}</span>
      </div>
    </td>
    <td
      class="px-4 py-2 text-gray-900 text-sm font-medium leading-5 border-b border-gray-200"
    >
      <div class="flex flex-col">
        <span v-if="!isInvitation">${{ getCredits() }}</span>
        <span v-else>--</span>
      </div>
    </td>
    <td
      class="px-4 py-2 font-medium text-xs leading-4 border-b border-gray-200"
    >
      <div class="relative inline-block group">
        <span
          class="px-2 rounded-lg"
          :class="
            isInvitation
              ? getInvitationStatusClass(getStatus())
              : getClassForStatus(row, originalInvitationRows)
          "
        >
          {{
            isInvitation
              ? invitationStatus(getStatus())
              : expertStatusText(
                  getJoinProgramRequestStatus(),
                  row,
                  originalInvitationRows
                )
          }}
        </span>
        <div
          class="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-max max-w-xs px-2 py-1 text-xs text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50 pointer-events-none"
        >
          <template v-if="isInvitation">
            {{
              getStatus() === "accepted"
                ? "Invitation was accepted"
                : getStatus() === "expired"
                ? "Invitation has expired"
                : "Invited via email to join your program."
            }}
          </template>
          <template v-else>
            {{
              getStatusTooltip(
                expertStatusText(
                  getJoinProgramRequestStatus(),
                  row,
                  originalInvitationRows
                )
              )
            }}
          </template>
        </div>
      </div>
    </td>
    <td
      class="px-4 py-2 font-medium text-xs leading-4 border-b border-gray-200"
    >
      {{
        isInvitation
          ? invitationLastActivityText(row)
          : lastActivityText(row, originalInvitationRows)
      }}
    </td>
    <td
      class="px-4 py-2 text-gray-500 font-normal leading-5 border-b border-gray-200 relative"
    >
      <div class="flex justify-center items-center min-h-[32px]">
        <template v-if="!(isInvitation && getStatus() === 'expired')">
          <div class="relative inline-block text-left">
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200 focus:outline-none"
              @mousedown.stop="toggleMenu"
              aria-haspopup="true"
              :aria-expanded="showMenu"
            >
              <span v-html="DOTS_VERTICAL_ICON"></span>
            </button>
            <div
              v-if="showMenu"
              class="origin-top-right absolute right-0 mt-2 min-w-[140px] rounded-md shadow-lg bg-white border border-gray-200 ring-1 ring-black ring-opacity-5 z-50 py-1"
              @mousedown.stop
            >
              <button
                type="button"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap overflow-hidden text-ellipsis"
                :disabled="disableButton"
                @mousedown="handleMenuAction"
              >
                {{ getActionButtonText() }}
              </button>
              <template v-if="showInactiveForNonAppliedUser()">
                <div class="border-t border-gray-200 my-1"></div>
                <button
                  type="button"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap overflow-hidden text-ellipsis"
                  :disabled="disableButton"
                  @mousedown="handleMakeInactiveClick"
                >
                  Make Inactive
                </button>
              </template>
              <template v-if="isInvitation">
                <div class="border-t border-gray-200 my-1"></div>
                <button
                  type="button"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap overflow-hidden text-ellipsis"
                  :disabled="disableButton"
                  @mousedown="handleCancelInvite"
                >
                  Cancel Invite
                </button>
              </template>
            </div>
          </div>
        </template>
        <template v-else>
          <!-- Empty placeholder for expired invitations to preserve row height/alignment -->
          <div style="height: 32px"></div>
        </template>
      </div>
    </td>
  </tr>
  <!-- Expanded content in a separate row -->
  <tr v-if="isExpandable() && isExpanded">
    <td colspan="6" class="bg-gray-50 text-left border-b border-gray-200">
      <div class="px-16 py-4">
        <div class="flex flex-row gap-8">
          <!-- First column with Meetings -->
          <div class="flex flex-col gap-2">
            <span class="text-sm text-left font-medium text-gray-600"
              >Meetings</span
            >
            <div
              class="flex gap-8 max-w-max border border-gray-300 px-2 py-1 rounded-md bg-white h-full"
            >
              <div class="flex items-center gap-2">
                <div
                  v-html="HAND_SHAKE_ICON"
                  class="w-8 h-8 text-primary-purple"
                ></div>
                <div class="flex flex-col text-sm text-gray-600">
                  <span class="font-medium">Past Meetings</span>
                  <span>{{ getPastMeetingsCount() }}</span>
                </div>
              </div>
              <!-- Vertical Divider -->
              <div class="h-12 w-px bg-gray-300"></div>
              <div class="flex items-center gap-2">
                <div
                  v-html="CALENDAR_CLOCK_ICON"
                  class="w-8 h-8 text-primary-purple"
                ></div>
                <div class="flex flex-col text-sm text-gray-600">
                  <span class="font-medium">Future Meetings</span>
                  <span>{{ getFutureMeetingsCount() }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- Second column -->
          <div class="flex flex-col gap-2">
            <span class="text-sm text-left font-medium text-gray-600"
              >Earnings</span
            >
            <div
              class="flex items-center gap-2 max-w-max border border-gray-300 px-2 py-1 rounded-md bg-white h-full"
            >
              <div
                v-html="DOLLAR_CIRCLE_ICON"
                class="w-8 h-8 text-primary-purple"
              ></div>
              <div class="flex flex-col text-sm text-gray-600">
                <span class="font-medium">Total Earnings</span>
                <span>${{ getTotalEarnings() }}</span>
              </div>
            </div>
          </div>
          <!-- Third column -->
          <div class="flex flex-col gap-2">
            <span class="text-sm text-left font-medium text-gray-600"
              >Tags</span
            >
            <div
              class="flex items-center gap-2 max-w-max border border-gray-300 px-2 py-1 rounded-md bg-white h-full"
            >
              <div
                v-html="STARS_ICON"
                class="w-8 h-8 text-primary-purple"
              ></div>
              <div class="flex flex-wrap gap-1 text-sm text-gray-600">
                <span
                  v-for="tag in getSelectedExpertTags()"
                  :key="tag"
                  class="px-2 py-1 bg-gray-100 rounded-md text-xs"
                >
                  {{ tag }}
                </span>
                <span
                  v-if="getSelectedExpertTags().length === 0"
                  class="text-gray-400"
                >
                  No tags selected
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </td>
  </tr>
  <div
    v-if="successMessage || errorMessage"
    :class="[
      'mt-2 text-xs px-3 py-2 rounded',
      messageType === 'success'
        ? 'bg-green-100 text-green-800'
        : 'bg-red-100 text-red-800',
    ]"
  >
    {{ successMessage || errorMessage }}
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  ref,
  onMounted,
  onUnmounted,
} from "vue";
import { Expert, Invitation } from "@/types/expert-programs-types";
import { RequestRow } from "@/types/dashboard-types";
import {
  BLANK_PROFILE_PICTURE_ICON,
  ARROW_DOWN,
  ARROW_UP,
  HAND_SHAKE_ICON,
  CALENDAR_CLOCK_ICON,
  DOLLAR_CIRCLE_ICON,
  STARS_ICON,
  HUBSPOT_LOGO_ICON,
  SALESFORCE_LOGO_ICON,
  DOTS_VERTICAL_ICON,
} from "@/assets/svg/shared/svg-constants";
import { useUserStore } from "@/stores/user";
import BaseButton from "@/components/shared/BaseButton.vue";
import { ApolloError, gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";

function isExpert(obj: Expert | Invitation | RequestRow): obj is Expert {
  console.log("check this email", (obj as Expert).email);
  console.log("check this object", obj);
  return (obj as Expert).firstName !== undefined;
}

function isInvitationObj(
  obj: Expert | Invitation | RequestRow
): obj is Invitation {
  return (obj as Invitation).userEmail !== undefined;
}

const EXPIRE_INVITATION = gql`
  mutation expireInvitation($input: ExpireInvitationInput!) {
    expireInvitation(input: $input) {
      success
    }
  }
`;

export default defineComponent({
  name: "ExpertRowItem",
  props: {
    row: {
      type: Object as PropType<Expert | Invitation | RequestRow>,
      required: true,
    },
    isInvitation: {
      type: Boolean,
      default: false,
    },
    originalInvitationRows: {
      type: Array as PropType<Invitation[]>,
      required: true,
    },
    shouldDisable: {
      type: Function,
      required: true,
    },
    getClassForStatus: {
      type: Function,
      required: true,
    },
    expertStatusText: {
      type: Function,
      required: true,
    },
    performActionText: {
      type: Function,
      required: true,
    },
    performAction: {
      type: Function,
      required: true,
    },
    handleMakeInactive: {
      type: Function,
      required: true,
    },
    lastActivityText: {
      type: Function,
      required: true,
    },
    invitationStatus: {
      type: Function,
      required: true,
    },
    invitationLastActivityText: {
      type: Function,
      required: true,
    },
    handleSendInvitation: {
      type: Function,
      required: true,
    },
    getStatusTooltip: {
      type: Function,
      required: true,
    },
    disableButton: {
      type: Boolean,
      default: false,
    },
    menuOpenId: {
      type: [String, Number, null],
      required: false,
      default: null,
    },
    setMenuOpenId: {
      type: Function,
      required: false,
    },
    rowId: {
      type: [String, Number],
      required: false,
    },
    refreshExperts: {
      type: Function,
      required: false,
    },
  },
  setup(props) {
    const userStore = useUserStore();
    const isExpanded = ref(false);
    const showMenu = computed(() => props.menuOpenId === props.rowId);
    const errorMessage = ref("");
    const successMessage = ref("");
    const messageType = ref(""); // 'success' or 'error'
    const {
      mutate: expireInvitation,
      onDone: expireInvitationDone,
      onError: expireInvitationError,
    } = useMutation(EXPIRE_INVITATION);

    const toggleMenu = (event?: MouseEvent) => {
      if (showMenu.value) {
        props.setMenuOpenId && props.setMenuOpenId(null);
      } else {
        props.setMenuOpenId && props.setMenuOpenId(props.rowId);
      }
    };

    const handleClickOutsideMenu = (event: MouseEvent) => {
      const menu = (event.target as HTMLElement).closest(".origin-top-right");
      const button = (event.target as HTMLElement).closest(
        ".inline-block.text-left"
      );
      if (!menu && !button && showMenu.value) {
        props.setMenuOpenId && props.setMenuOpenId(null);
      }
    };

    onMounted(() => {
      document.addEventListener("mousedown", handleClickOutsideMenu);
    });
    onUnmounted(() => {
      document.removeEventListener("mousedown", handleClickOutsideMenu);
    });

    const handleMenuAction = () => {
      props.setMenuOpenId && props.setMenuOpenId(null);
      handleClick();
    };

    const handleCancelInvite = () => {
      props.setMenuOpenId && props.setMenuOpenId(null);
      if (props.isInvitation && props.row && props.row.id) {
        expireInvitation({ input: { id: props.row.id } });
        expireInvitationDone((response) => {
          if (response?.data?.expireInvitation?.success) {
            if (props.refreshExperts) props.refreshExperts();
            successMessage.value = "Invitation cancelled successfully.";
            messageType.value = "success";
            setTimeout(() => {
              successMessage.value = "";
              messageType.value = "";
              window.location.reload();
            }, 2500);
          } else {
            errorMessage.value = "Failed to expire invitation.";
            messageType.value = "error";
            setTimeout(() => {
              errorMessage.value = "";
              messageType.value = "";
              window.location.reload();
            }, 2500);
          }
        });
        expireInvitationError((error: ApolloError) => {
          errorMessage.value = error.message;
          messageType.value = "error";
          setTimeout(() => {
            errorMessage.value = "";
            messageType.value = "";
            window.location.reload();
          }, 2500);
        });
      }
    };

    const getInvitationStatusClass = (
      status: string
    ): Record<string, boolean> => {
      return {
        "text-green-800 bg-green-100": status === "accepted",
        "text-red-800 bg-red-100": status === "expired",
        "text-gray-600 bg-gray-200": status === "pending",
      };
    };

    const getJoinProgramRequestStatus = (): string => {
      if (props.isInvitation) return "";
      return isExpert(props.row) && props.row.joinProgramRequest?.[0]?.status
        ? props.row.joinProgramRequest[0].status
        : "";
    };

    const getStatus = (): string => {
      return props.isInvitation && isInvitationObj(props.row)
        ? props.row.status
        : "";
    };

    const getName = (): string => {
      if (props.isInvitation) return "";
      return isExpert(props.row)
        ? `${props.row.firstName} ${props.row.lastName}`
        : "";
    };

    const getTitle = (): string => {
      if (props.isInvitation) return "";
      return isExpert(props.row) ? props.row.title || "" : "";
    };

    const getCompanyName = (): string => {
      if (props.isInvitation) return "";
      return isExpert(props.row) ? props.row.companyName || "" : "";
    };

    const getEmail = (): string => {
      console.log("check this row", props.row);
      if (props.isInvitation) {
        return isInvitationObj(props.row) ? props.row.userEmail : "";
      }
      return isExpert(props.row) ? props.row.email : "";
    };

    const getCredits = (): number => {
      if (props.isInvitation) return 0;
      return isExpert(props.row) ? props.row.credits : 0;
    };

    const handleClick = (): void => {
      if (props.isInvitation) {
        if (isInvitationObj(props.row)) {
          props.handleSendInvitation(props.row.userEmail);
        }
      } else {
        props.performAction(props.row);
      }
    };

    const getActionButtonText = (): string => {
      if (props.isInvitation) {
        return "Send Reminder to Complete Signup";
      }
      return props.performActionText(
        getJoinProgramRequestStatus(),
        props.row,
        props.originalInvitationRows
      );
    };

    const showInactiveForNonAppliedUser = (): boolean => {
      if (props.isInvitation) {
        return false;
      }
      const status = props.expertStatusText(
        getJoinProgramRequestStatus(),
        props.row,
        props.originalInvitationRows
      );

      if (
        status !== "Active" &&
        status !== "Pending" &&
        status !== "Inactive"
      ) {
        return true;
      }
      return false;
    };

    const handleMakeInactiveClick = (): void => {
      props.setMenuOpenId && props.setMenuOpenId(null);
      props.handleMakeInactive(props.row);
    };

    const isExpired = (): boolean => {
      return (
        props.isInvitation &&
        isInvitationObj(props.row) &&
        props.row.status === "expired"
      );
    };

    const hasProfilePicture = (): boolean => {
      if (props.isInvitation) return false;
      return isExpert(props.row) && !!props.row.profile_picture;
    };

    const getProfilePicture = (): string => {
      if (!hasProfilePicture()) return "";
      return isExpert(props.row) ? props.row.profile_picture : "";
    };

    const hasLinkedinProfile = (): boolean => {
      if (props.isInvitation) return false;
      return isExpert(props.row) && !!props.row.linkedin_profile;
    };

    const handleLinkedinClick = (): void => {
      if (isExpert(props.row) && props.row.linkedin_profile) {
        const url = props.row.linkedin_profile.trim();
        const finalUrl = /^(http:\/\/|https:\/\/)/i.test(url)
          ? url
          : `https://${url}`;
        window.open(finalUrl, "_blank");
      }
    };

    const toggleExpand = (): void => {
      isExpanded.value = !isExpanded.value;
    };

    const getPastMeetingsCount = (): number => {
      if (props.isInvitation) return 0;
      return isExpert(props.row) ? props.row.pastMeetingsCount : 0;
    };

    const getFutureMeetingsCount = (): number => {
      if (props.isInvitation) return 0;
      return isExpert(props.row) ? props.row.futureMeetingsCount : 0;
    };

    const getTotalEarnings = (): number => {
      if (props.isInvitation) return 0;
      return isExpert(props.row) ? props.row.totalEarnings : 0;
    };

    const isExpandable = (): boolean => {
      return (
        !props.isInvitation &&
        isExpert(props.row) &&
        props.row.onboardingStep === "completed" &&
        (props.row.joinProgramRequest?.[0]?.status === "approved" ||
          props.row.joinProgramRequest?.[0]?.status === "archived")
      );
    };

    const getSelectedExpertTags = (): string[] => {
      if (props.isInvitation) return [];
      return isExpert(props.row)
        ? props.row.programProfileDetails?.selectedExpertTags || []
        : [];
    };

    const hasCrmContact = (): boolean => {
      if (props.isInvitation) return false;
      return (
        isExpert(props.row) &&
        !!props.row.programsCrmContact &&
        !!props.row.programsCrmContact[userStore.selectedProgram?.id || ""]
      );
    };

    const getCrmType = (): string => {
      if (props.isInvitation) return "";
      return isExpert(props.row)
        ? props.row.programsCrmContact[userStore.selectedProgram?.id || ""]
            ?.crm_type || ""
        : "";
    };

    const getCrmUrl = (): string => {
      if (props.isInvitation) return "";
      return isExpert(props.row)
        ? props.row.programsCrmContact[userStore.selectedProgram?.id || ""]
            ?.contact_url || ""
        : "";
    };

    const openCrmContact = (): void => {
      if (props.isInvitation || !hasCrmContact()) return;
      if (getCrmUrl()) {
        window.open(getCrmUrl(), "_blank");
      }
    };

    const programSlug = computed(() => {
      const program = userStore.selectedProgram;
      if (!program) return null;
      return program.slug;
    });

    const getExpertCalendarUrl = () => {
      if (!isExpert(props.row)) return "#";
      if (!props.row.slug || !programSlug.value) return "#";
      return `/${programSlug.value}/${props.row.slug}`;
    };

    const getExpertName = () => {
      if (!isExpert(props.row)) return "";
      return `${props.row.firstName} ${props.row.lastName}`;
    };

    const shouldShowExpertLink = () => {
      if (!isExpert(props.row)) return false;
      return props.row.slug && programSlug.value;
    };

    return {
      BLANK_PROFILE_PICTURE_ICON,
      ARROW_DOWN,
      ARROW_UP,
      HAND_SHAKE_ICON,
      CALENDAR_CLOCK_ICON,
      DOLLAR_CIRCLE_ICON,
      STARS_ICON,
      HUBSPOT_LOGO_ICON,
      SALESFORCE_LOGO_ICON,
      DOTS_VERTICAL_ICON,
      userStore,
      isExpanded,
      isExpert,
      getInvitationStatusClass,
      getJoinProgramRequestStatus,
      getStatus,
      getName,
      getTitle,
      getCompanyName,
      getEmail,
      getCredits,
      handleClick,
      getActionButtonText,
      isExpired,
      hasProfilePicture,
      getProfilePicture,
      hasLinkedinProfile,
      handleLinkedinClick,
      toggleExpand,
      getPastMeetingsCount,
      getFutureMeetingsCount,
      getTotalEarnings,
      isExpandable,
      getSelectedExpertTags,
      hasCrmContact,
      getCrmType,
      getCrmUrl,
      openCrmContact,
      programSlug,
      getExpertCalendarUrl,
      getExpertName,
      shouldShowExpertLink,
      showMenu,
      toggleMenu,
      handleMenuAction,
      handleCancelInvite,
      handleMakeInactiveClick,
      showInactiveForNonAppliedUser,
      errorMessage,
      successMessage,
      messageType,
    };
  },
});
</script>
